import { Fragment } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { useCompanies } from '../shared/companies';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import type { PreOrderInput } from './preOrderApi';
import { ContractorPicker } from './ContractorPicker';
import { FEATURE_PRE_ORDER_CONTRACTOR, type ContractorRef } from './preOrderContractorApi';
import { ContractTestId } from '../usage/ContractTestId';
import {
  fieldLabel,
  questionAbout,
  requiredInputAttrs,
  useRequiredFields,
  type OpenQuestion,
} from '../shared/requiredFields';

/**
 * Pola nagłówka prezlecenia — wspólne dla wprowadzania i poprawiania.
 *
 * ## Dlaczego nic nie jest „wymagane" w formularzu
 *
 * Prezlecenie wolno zapisać niekompletne: spedytor coś wie od razu, resztę
 * dopytuje. Blokowanie zapisu do czasu kompletu zmusiłoby go do trzymania
 * połowy zlecenia w głowie albo do wpisywania wartości zastępczych — a to
 * drugie jest gorsze niż pusta rubryka, bo wygląda jak ustalenie.
 *
 * Komplet jest wymagany dopiero przy **akceptacji**, i wtedy serwer wypisuje,
 * czego brakuje.
 */

/**
 * Etykiety takie same jak nazwy braków z API — żeby dało się je zestawić.
 * Czwarty element to podpowiedź pod polem.
 */
export const PRE_ORDER_FORM_FIELDS: Array<[keyof PreOrderInput, string, string?, string?]> = [
  ['clientName', 'Klient'],
  // Numer zamówienia klienta (16.10.2026, odpowiedzi Marathonu/Euvic,
  // pyt. 27): VIA nadaje przy imporcie własny numer, a zachowuje ten.
  [
    'customerOrderNumber',
    'Nr zamówienia klienta',
    undefined,
    'Numer z maila klienta (referencyjny albo załadunku). VIA nada swój numer, a po tym rozpoznamy zlecenie w TMS.',
  ],
  ['relation', 'Relacja'],
  ['loadingCity', 'Miasto załadunku'],
  // Kraj i kod pocztowy (06.10.2026) — z nich geokodowanie wyznacza punkt
  // na mapie, a z nim dolot w rekomendacjach. Bez kraju punktu nie ma.
  ['loadingCountry', 'Kraj załadunku (np. PL)'],
  ['loadingPostalCode', 'Kod pocztowy załadunku'],
  ['unloadingCity', 'Miasto rozładunku'],
  ['unloadingCountry', 'Kraj rozładunku (np. DE)'],
  ['unloadingPostalCode', 'Kod pocztowy rozładunku'],
  ['loadingDate', 'Data załadunku', 'datetime-local'],
  ['unloadingDate', 'Data rozładunku', 'datetime-local'],
  // Wymagany typ pojazdu jak w TMS (16.10.2026, pyt. 39e).
  [
    'requiredVehicleType',
    'Wymagany typ pojazdu',
    undefined,
    'Jak w TMS, np. JUMBO TRUCK, MEGA TRUCK. Z wózkiem: JUMBO TRUCK+FORKLIFT — do VIA pójdzie wtedy drugi plik z czynnością wózkową.',
  ],
  ['cargo', 'Ładunek'],
  ['weight', 'Waga (kg)', 'number'],
  ['pallets', 'Palety', 'number'],
  ['price', 'Cena', 'number'],
  ['currency', 'Waluta'],
  ['responsibleDept', 'Dział'],
];

const asText = (v: unknown) => (v === null || v === undefined ? '' : String(v));

export const PreOrderForm = ({
  values,
  onChange,
  onContractorChange,
  locked = false,
  openQuestion = null,
}: {
  values: PreOrderInput;
  onChange: (field: keyof PreOrderInput, v: string) => void;
  /**
   * Kontrahent z kartoteki (fala B, 21.10.2026, `preOrderContractor`) — pole
   * stoi w formularzu, gdy przełącznik działa i wołający obsługuje wybór.
   */
  onContractorChange?: (c: ContractorRef | null) => void;
  locked?: boolean;
  /**
   * Otwarte pytanie Administracji przypięte do pola (24.10.2026,
   * `intakeQuestionField`) — pole dostaje treść pytania pod sobą.
   */
  openQuestion?: OpenQuestion | null;
}) => {
  const companies = useCompanies();
  const contractorOn = useFeature(FEATURE_PRE_ORDER_CONTRACTOR);
  // Pola obowiązkowe półgrubą czcionką (24.10.2026, `requiredFieldsHighlight`).
  const isRequired = useRequiredFields();
  /** Pod polem: pytanie Administracji, a bez niego — podpowiedź jak dotąd. */
  const hintFor = (key: string, hint?: string) =>
    questionAbout(openQuestion, [key]) ? `Pytanie Administracji: ${openQuestion?.message ?? '—'}` : hint;
  const hintSlot = (key: string) =>
    questionAbout(openQuestion, [key])
      ? { formHelperText: { sx: { color: 'warning.main' }, 'data-testid': `intake-question-marker-${key}` } as object }
      : {};

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1,
        // Liczba kolumn z szerokości KARTY, nie ekranu: w „Popraw dane” karta
        // zajmuje pół ekranu i trzy kolumny przy 1366 px ucinały wpisane
        // wartości („TEST-CLAUDE 28.0”). Pole ma co najmniej 220 px.
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
      }}
    >
      <TextField
        select
        size="small"
        label={fieldLabel('Spółka', isRequired('companyId'), { forInput: true })}
        value={asText(values.companyId)}
        disabled={locked}
        onChange={(e) => onChange('companyId', e.target.value)}
        helperText={hintFor('companyId', 'Decyduje, pod jakim identyfikatorem zlecenie pójdzie do VIA')}
        slotProps={{ ...hintSlot('companyId'), htmlInput: requiredInputAttrs(isRequired('companyId')) }}
      >
        {companies.map((s) => (
          <MenuItem key={s.id} value={String(s.id)}>
            {s.name}
            {s.country ? ` (${s.country})` : ''}
          </MenuItem>
        ))}
      </TextField>

      {PRE_ORDER_FORM_FIELDS.map(([field, label, inputType, hint]) => (
        <Fragment key={String(field)}>
          <TextField
            size="small"
            label={fieldLabel(label, isRequired(String(field)), { forInput: true })}
            type={inputType ?? 'text'}
            value={asText(values[field])}
            disabled={locked}
            helperText={hintFor(String(field), hint)}
            // Etykieta pola daty musi być zwinięta od początku — inaczej
            // nakłada się na podpowiedź formatu.
            // Kontrakt `data-testid` paczki (S-22.10-02): godziny rozładunku (u nas jeden punkt, jedna chwila).
            slotProps={{
              ...hintSlot(String(field)),
              htmlInput: {
                ...requiredInputAttrs(isRequired(String(field))),
                ...(field === 'unloadingDate' ? { 'data-testid': 'cs-zlecenie-punkt-2-godziny' } : {}),
              },
              ...(inputType === 'datetime-local' ? { inputLabel: { shrink: true } } : {}),
            }}
            onChange={(e) => onChange(field, e.target.value)}
          />
          {field === 'clientName' && contractorOn && onContractorChange ? (
            // Zaraz po nazwie klienta — wyszukiwarka startuje od niej (BR-ADM-13).
            <Box sx={{ gridColumn: '1 / -1' }}>
              {/* Kontrakt `data-testid` paczki (S-22.10-02): oddział kontrahenta — wybór z kartoteki (pole ma nasze identyfikatory). */}
              <ContractTestId id="cs-zlecenie-oddzial" block>
                <ContractorPicker
                  value={values.contractor ?? null}
                  onChange={onContractorChange}
                  suggestFrom={values.clientName ?? null}
                  disabled={locked}
                />
              </ContractTestId>
            </Box>
          ) : null}
        </Fragment>
      ))}
    </Box>
  );
};
